/** Shapes the intake screens read that no other part of the app does. */
import { z } from 'zod';
import { fileRecordSchema } from './clients';

/** `intakeService.queue()` — one row per client on the intake board. */
export const intakeQueueEntrySchema = z.object({
	client: z.object({
		pkClientId: z.string(),
		name: z.string(),
		code: z.string(),
		erp: z.string().nullable(),
	}),
	files: z.number(),
	awaitingMapping: z.number(),
	processing: z.number(),
	failed: z.number(),
	awaitingReplacement: z.number(),
	ready: z.number(),
	rows: z.number(),
	lastUploadAt: z.string().nullable(),
	activeAudits: z.number(),
	rememberedLayouts: z.number(),
});

export const processingStepSchema = z.object({
	stage: z.string(),
	status: z.string(),
	detail: z.string().nullable(),
	startedAt: z.string().nullable(),
	endedAt: z.string().nullable(),
});

/** `GET /processing-runs/:id` — the run, plus what the screen draws around it. */
export const runViewSchema = z.object({
	pkProcessingRunId: z.string(),
	fkFileId: z.string(),
	status: z.string(),
	progress: z.number(),
	progressNote: z.string().nullable(),
	rowCount: z.number().nullable(),
	errorMessage: z.string().nullable(),
	startedAt: z.string().nullable(),
	finishedAt: z.string().nullable(),
	fileName: z.string(),
	fileStatus: z.string(),
	steps: z.array(processingStepSchema),
	classification: z.object({
		sales: z.number(),
		returns: z.number(),
		adjustments: z.number(),
		unclassified: z.number(),
		total: z.number(),
		classifiedAt: z.string().nullable(),
	}),
});

/** `GET /files/:id/ingest` and `GET /file-ingest-runs/:id` — the upload read,
 *  as something a person can watch. */
export const ingestStepSchema = z.object({
	stage: z.string(),
	status: z.string(),
	detail: z.string().nullable(),
	/* As the worker measured it, not the gap between the timestamps below. */
	durationMs: z.number().nullable(),
	startedAt: z.string().nullable(),
	endedAt: z.string().nullable(),
});

export const ingestRunSchema = z.object({
	runId: z.string(),
	fileId: z.string(),
	status: z.string(),
	progressPct: z.number(),
	progressNote: z.string().nullable(),
	errorMessage: z.string().nullable(),
	rowCount: z.number().nullable(),
	columnCount: z.number().nullable(),
	startedAt: z.string().nullable(),
	finishedAt: z.string().nullable(),
	/* The server's judgement: only it sees the worker's heartbeat. */
	stale: z.boolean(),
	steps: z.array(ingestStepSchema),
	/** Every stage's measured time added up, not the age of the run. */
	totalMs: z.number(),
});

export const latestIngestSchema = z.object({
	run: ingestRunSchema.nullable(),
});

export const previewRuleSchema = z.object({
	id: z.string(),
	priority: z.number(),
	fkClientId: z.string().nullable(),
	matchField: z.string(),
	matchOperator: z.string(),
	matchValue: z.string(),
	classification: z.string(),
	matchCount: z.number(),
});

export const previewRowSchema = z.object({
	values: z.array(z.union([z.string(), z.number(), z.null()])),
	ruleId: z.string().nullable(),
	classification: z.string().nullable(),
});

export const classificationPreviewSchema = z.object({
	columns: z.array(z.string()),
	rules: z.array(previewRuleSchema),
	rows: z.array(previewRowSchema),
	/* How many rows the endpoint asked for, not how many it got back. */
	sampleSize: z.number(),
});

/** A file, plus when it was last classified. */
export const fileWithClassifiedAtSchema = fileRecordSchema.extend({
	classifiedAt: z.string().nullable(),
});

/** `GET /firm/classification-rules`. Deliberately not the same field names as
 *  `ClassificationRule`, which mirrors the raw Prisma row; both are real. */
export const wireClassificationRuleSchema = z.object({
	pkClassificationRuleId: z.string(),
	fkRulesetId: z.string(),
	fkClientId: z.string().nullable(),
	priority: z.number(),
	matchField: z.string(),
	matchOperator: z.string(),
	matchValue: z.string(),
	classification: z.string(),
	reason: z.string(),
	createdAt: z.string(),
});

/** Which rules a file was actually classified with. */
export const fileClassificationRuleRecordSchema = z.object({
	classifiedAt: z.string().nullable(),
	recorded: z.array(
		z.object({
			ruleId: z.string(),
			matchField: z.string().nullable(),
			matchOperator: z.string().nullable(),
			matchValue: z.string().nullable(),
			classification: z.string().nullable(),
			stillPublished: z.boolean(),
		}),
	),
	ruleIds: z.array(z.string()),
	hasSelection: z.boolean(),
});

export const reapplyResultSchema = z.object({
	before: z.record(z.string(), z.number()),
	after: z.record(z.string(), z.number()),
	rulesApplied: z.number(),
});

export const fileSampleSchema = z.object({
	columns: z.array(z.string()),
	rows: z.array(z.array(z.union([z.string(), z.number(), z.null()]))),
});

export type IntakeQueueEntry = z.infer<typeof intakeQueueEntrySchema>;
export type ProcessingStep = z.infer<typeof processingStepSchema>;
export type IngestRun = z.infer<typeof ingestRunSchema>;
export type IngestStep = z.infer<typeof ingestStepSchema>;
export type RunView = z.infer<typeof runViewSchema>;
export type PreviewRule = z.infer<typeof previewRuleSchema>;
export type PreviewRow = z.infer<typeof previewRowSchema>;
export type ClassificationPreview = z.infer<typeof classificationPreviewSchema>;
export type FileWithClassifiedAt = z.infer<typeof fileWithClassifiedAtSchema>;
export type WireClassificationRule = z.infer<
	typeof wireClassificationRuleSchema
>;
export type FileClassificationRuleRecord = z.infer<
	typeof fileClassificationRuleRecordSchema
>;
export type ReapplyResult = z.infer<typeof reapplyResultSchema>;
export type FileSample = z.infer<typeof fileSampleSchema>;

/** `GET /files/:fileId/columns` — the sheet's columns in order, each with the
 *  standard column it is mapped to (null: not mapped), and the mapping they
 *  come from (null until one is confirmed). */
export const fileColumnsSchema = z.object({
	mapping: z
		.object({
			version: z.number(),
			confirmedAt: z.string().nullable(),
			confirmedByName: z.string().nullable(),
		})
		.nullable(),
	columns: z.array(
		z.object({
			position: z.number(),
			name: z.string(),
			dataType: z.string(),
			samples: z.array(z.string()),
			mappedTo: z
				.object({ field: z.string(), label: z.string() })
				.nullable(),
		}),
	),
});
export type FileColumns = z.infer<typeof fileColumnsSchema>;
